import React, { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Collapse, Grid, IconButton, Typography } from '@mui/material';
import { default as ExpandMoreIcon } from '@mui/icons-material/ExpandMore';
import { default as ExpandLessIcon } from '@mui/icons-material/ExpandLess';
import DeleteControls from '../../../../common/DeleteControls';
import LevelButtonBar from './LevelButtonBar';
import {
  DndProps,
  useDnd,
  verticalListSortingStrategy,
} from '../../../hooks/useSortableDnd';
import { generateDataTestId } from 'src/utils';
import { useMetadata } from '../../../../../contexts/MetadataContext';
import { PropertyField, RoomField } from '../../../../../system/field';
import {
  LevelType,
  normalizeLevel,
  Room,
  RoomGroupType,
} from '../../../../../system/rooms';
import { useFeatureFlags } from '../../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../../flags';
import { getEmptyLevelKey } from '../utils';
import LevelCountInput from './LevelCountInput';
import { useRoomsSimulatedValidation } from '../useSimulatedValidation';

interface LevelProps {
  canAddRooms?: boolean;
  confirmingDelete: string | null;
  disabled?: boolean;
  expandedLevel: boolean;
  handleProps?: any;
  highlightedRoomTypes?: string[];
  inDeleteMode: boolean;
  isDragging?: boolean;
  isHover?: boolean;
  level: string;
  onAddNewRoom?: (type: string, level: string) => void;
  onAddRoom?: () => void;
  onCancelDelete?: () => void;
  onConfirmDelete?: () => void;
  onDeleteLevel?: (level: string) => void;
  onDeleteRooms?: (rooms: Room[]) => void;
  onEditRoom?: (rooms: Room) => void;
  onRequestDelete?: () => void;
  renderRoom: (dndProps: DndProps, room: Room) => React.ReactElement;
  rooms: Room[];
  roomGroups: RoomGroupType[];
  toggleLevelExpanded: (level: string) => void;
}

export const normalizeLevelLabel = (level: string) => {
  const match = /(upper|lower)\s*(level)?\s*(\d+)/gi.exec(level);
  if (match) {
    return [match[1], 'Level', match[3]].join(' ');
  }

  return level.endsWith('Level') ? level : `${level} Level`;
};

const LevelDetails = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: theme.palette.background.pale,
  marginLeft: theme.spacing(0),
  [theme.breakpoints.up('md')]: {
    marginLeft: theme.spacing(4),
  },
}));

const Level: React.FC<LevelProps> = React.memo(
  ({
    canAddRooms = false,
    confirmingDelete = '',
    expandedLevel,
    handleProps,
    highlightedRoomTypes,
    inDeleteMode,
    isDragging = false,
    isHover: _isHover = false,
    level,
    onAddNewRoom,
    onAddRoom = () => {},
    onCancelDelete = () => {},
    onConfirmDelete = () => {},
    onRequestDelete = () => {},
    renderRoom,
    rooms,
    roomGroups,
    toggleLevelExpanded,
    disabled = false,
  }) => {
    const { SortableContext, SortableItem } = useDnd();
    const { field: getFieldMetadata, displayValueForListingField } =
      useMetadata();
    const { hasFeature } = useFeatureFlags();
    const hasUserEnteredCounts = hasFeature(
      FeatureFlags.NGW_ROOMS_USER_ENTERED_COUNTS,
    );
    const roomsSimulated = useRoomsSimulatedValidation();

    // Translate the level value if it's a lookup value
    const translatedLevel = useMemo(() => {
      const metadata = getFieldMetadata(RoomField.ROOM_LEVEL);
      if (metadata?.Lookup && level) {
        return displayValueForListingField(metadata.Lookup, level);
      }
      return level;
    }, [level, getFieldMetadata, displayValueForListingField]);

    // Map this level to its 3 per-level property field keys. Main level uses
    // the dedicated MAIN_LEVEL_* fields; Upper/Lower N uses the indexed keys
    // from getEmptyLevelKey.
    const levelFieldKeys = useMemo(() => {
      if (!hasUserEnteredCounts) return null;
      const normalized = normalizeLevel(level);
      if (normalized.type === LevelType.MAIN) {
        return {
          bedrooms: PropertyField.MAIN_LEVEL_BEDROOMS,
          fullBaths: PropertyField.MAIN_LEVEL_FULL_BATHS,
          halfBaths: PropertyField.MAIN_LEVEL_HALF_BATHS,
        };
      }
      if (!normalized.number) return null;
      return {
        bedrooms: getEmptyLevelKey(
          normalized.type,
          normalized.number,
          'BEDROOMS',
        ),
        fullBaths: getEmptyLevelKey(
          normalized.type,
          normalized.number,
          'FULL_BATHS',
        ),
        halfBaths: getEmptyLevelKey(
          normalized.type,
          normalized.number,
          'HALF_BATHS',
        ),
      };
    }, [hasUserEnteredCounts, level]);

    const isMain = translatedLevel.toLowerCase().includes('main');
    const levelLabel = normalizeLevelLabel(translatedLevel);
    return (
      <SortableContext
        items={rooms.map((room: Room) => room.ROOM_ITEM_NUMBER)}
        strategy={verticalListSortingStrategy}
      >
        <Box
          sx={{
            width: 1,
            mb: 1,
            backgroundColor: 'white',
            opacity: isDragging ? 0.5 : 1,
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexGrow: 1,
              mb: 1,
            }}
          >
            <Box
              alignItems="center"
              display="flex"
              onClick={() => toggleLevelExpanded(level)}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLevelExpanded(level);
                }}
              >
                {expandedLevel ? (
                  <ExpandLessIcon sx={{ color: 'text.secondary' }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
                )}
              </IconButton>
              <Typography
                data-testid={generateDataTestId(
                  levelLabel,
                  undefined,
                  'levelHeader',
                )}
                variant="h4"
                component="span"
                sx={{
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '24px',
                  ml: 1,
                  cursor: 'pointer',
                }}
              >
                {levelLabel}
                {!hasUserEnteredCounts && ` (${rooms.length} Rooms)`}
              </Typography>
            </Box>
            <Box sx={{ ml: 2 }}>
              {inDeleteMode && !isMain && (
                <DeleteControls
                  deleteButtonText="Delete Level"
                  disabled={false}
                  onCancelDelete={onCancelDelete}
                  onConfirmDelete={
                    confirmingDelete?.length ? onConfirmDelete : onRequestDelete
                  }
                  isConfirming={
                    !!confirmingDelete?.length && confirmingDelete === level
                  }
                />
              )}
            </Box>
          </Box>
          <Collapse in={!!expandedLevel}>
            <LevelDetails
              sx={{
                display: expandedLevel ? 'flex' : 'none',
                flexDirection: 'column',
              }}
            >
              {levelFieldKeys && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <LevelCountInput
                      fieldKey={levelFieldKeys.bedrooms as PropertyField}
                      label="Beds"
                      isError={roomsSimulated.bedroomsMissing}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <LevelCountInput
                      fieldKey={levelFieldKeys.fullBaths as PropertyField}
                      label="Baths"
                      isError={roomsSimulated.fullBathsMissing}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <LevelCountInput
                      fieldKey={levelFieldKeys.halfBaths as PropertyField}
                      label="Half Baths"
                    />
                  </Grid>
                </Grid>
              )}
              <Box
                sx={{
                  mb: rooms.length ? 2 : 0,
                }}
              >
                <LevelButtonBar
                  canAddRooms={canAddRooms}
                  handleProps={handleProps}
                  highlightedRoomTypes={highlightedRoomTypes}
                  isDragging={isDragging}
                  onAddNewRoom={(roomType: string) => {
                    onAddNewRoom?.(roomType, level);
                  }}
                  onAddRoom={onAddRoom}
                  rooms={rooms}
                  roomGroups={roomGroups}
                  level={levelLabel}
                  disabled={disabled}
                />
              </Box>
              <Grid container columnSpacing={2}>
                {rooms.map((room, index) => (
                  <SortableItem
                    key={room.ROOM_ITEM_NUMBER}
                    id={room.ROOM_ITEM_NUMBER}
                    index={index}
                    renderItem={(dndProps: DndProps) => {
                      return renderRoom(dndProps, room);
                    }}
                    disabled={rooms.length <= 1}
                  />
                ))}
              </Grid>
            </LevelDetails>
          </Collapse>
        </Box>
      </SortableContext>
    );
  },
);

export default Level;
